import type { Hit, TypesenseProduct } from "@/lib/typesense";

import {
  ProductCardWithPriceComparison,
  type CardDensity
} from "@/components/product/ProductCardWithPriceComparison";
import { useProductsWithAccuratePricingResults } from "@/hooks";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useSellersMap } from "@/queries/sellers";
import {
  findVariantMatchingSearchQuery,
  formattedProductOverviewData
} from "@/utils/product";
import { useMemo } from "react";

interface Props {
  items: Hit<TypesenseProduct>[];
  /**
   * Supplier names currently selected in the sidebar filter (the URL
   * `suppliers` param). When set, the card pins those suppliers to the top of
   * its supplier table. See salonstocker/storefront#82.
   */
  selectedSuppliers?: string[];
  /**
   * The active free-text search query (the URL `q` param). When it names a
   * variant precisely (e.g. a shade code - "redken eq gloss 5g"), the card's
   * variant selector defaults to that variant instead of the product-wide
   * aggregate. See `findVariantMatchingSearchQuery`.
   */
  searchQuery?: string;
}

export function SearchResults({
  items,
  selectedSuppliers,
  searchQuery
}: Props) {
  // One query drives both the grid and the cards' density on purpose: they have
  // to agree. A 2-up grid halves each card's width, so a cell laying out the
  // wide `full` card would overflow its own column - the two are one decision,
  // not two. See storefront#145 (P4).
  const isTwoUp = useBreakpoint("xl");
  const isSingleColumnDesktop = useBreakpoint("lg") && !isTwoUp;
  const density: CardDensity = isSingleColumnDesktop
    ? "full"
    : isTwoUp
      ? "twoUp"
      : "compact";

  const { data: sellersMap } = useSellersMap();
  const { products, offerPriceState } =
    useProductsWithAccuratePricingResults(items);

  const selectedSupplierNames = useMemo(
    () => new Set(selectedSuppliers ?? []),
    [selectedSuppliers]
  );

  // Derived once per products/filter/query change rather than on every
  // SearchResults render (e.g. toggling the filters sidebar re-renders this
  // component without changing any of the above) - keeps `pinnedSellerIds`
  // referentially stable so the memoized `ProductCardWithPriceComparison`
  // below - and its own offer/price `useMemo`s - only recompute when
  // something here actually changed.
  const cards = useMemo(
    () =>
      products.map((product) => {
        const formattedProductProps = formattedProductOverviewData(
          product,
          sellersMap
        );

        // The product-level sellers matching the active supplier filter, by
        // id. Passed straight through to the card, which pins them to the
        // top of the supplier table regardless of which variant (or "All")
        // is selected. See salonstocker/storefront#82.
        const pinnedSellerIds = selectedSupplierNames.size
          ? new Set(
              product.sellers
                .filter((s) => selectedSupplierNames.has(s.name))
                .map((s) => s.id)
            )
          : undefined;

        const matchedVariant = findVariantMatchingSearchQuery(
          product.variants,
          searchQuery
        );
        // Don't auto-select a variant no supplier stocks - the card would
        // have nothing to show but an empty comparison table. Fall back to
        // the "All variants" aggregate instead; the unavailable variant is
        // still visible (greyed out) in the selector if the buyer wants it.
        const defaultVariantId =
          matchedVariant && matchedVariant.seller_offers.length > 0
            ? matchedVariant.id
            : undefined;

        return {
          ...formattedProductProps,
          variants: product.variants,
          pinnedSellerIds,
          defaultVariantId
        };
      }),
    [products, sellersMap, selectedSupplierNames, searchQuery]
  );

  return (
    <div
      className={
        isTwoUp
          ? // Each `twoUp` card spans two rows (header, supplier table) as a
            // subgrid, so both cards' rows share heights and their lines
            // align. `items-start` keeps a card from stretching past them.
            "grid grid-cols-2 items-start gap-x-4 gap-y-4"
          : "space-y-3 sm:space-y-4 lg:space-y-5"
      }
    >
      {cards.map((card) => (
        <ProductCardWithPriceComparison
          key={card.productId}
          {...card}
          offerPriceState={offerPriceState}
          density={density}
        />
      ))}
    </div>
  );
}
